/**
 * TrustGates — Trust (Revocable, Irrevocable, QIT, SNT).
 *
 * Ported from SPFx ProceduralGates:
 *   - _validateRequiredFields (RevocableTrust / IrrevocableTrust / QIT / SNT)
 *   - _validateRelationships (TRUST_TRUSTEE — at least 1 trustee signer)
 *
 * Plus v3 additions: trust certification document, CIP on each trustee.
 */

import type { CartSnapshot, GateResult } from '../workflow-engine/types';
import {
  buildCipGate,
  buildOfacGate,
  buildRequiredFieldGates,
  getByRole,
  getPrimary,
  type FieldSpec,
} from '../ProceduralGates';

const TRUST_FIELDS: ReadonlyArray<FieldSpec> = [
  { field: 'name2', label: 'Trust name' },
  { field: 'taxId', label: 'Trust Tax ID (SSN for grantor trust, EIN for irrevocable)' },
  { field: 'addr', label: 'Street address' },
  { field: 'city', label: 'City' },
  { field: 'state', label: 'State' },
  { field: 'zip', label: 'ZIP code' },
];

export function runTrustGates(cart: CartSnapshot): GateResult[] {
  const gates: GateResult[] = [];
  const primary = getPrimary(cart);
  if (!primary) return gates;

  gates.push(...buildRequiredFieldGates(cart, primary, TRUST_FIELDS));

  // Trustee / authorized signer — at least 1 (SPFx TRUST_TRUSTEE).
  const trustees = getByRole(cart, 'trustee');
  const signers = getByRole(cart, 'signer');
  if (trustees.length + signers.length === 0) {
    gates.push({
      gateCode: 'TRUST_TRUSTEE',
      entityType: cart.entityType ?? '',
      category: 'relationship',
      status: 'fail',
      label: 'Trust requires 1+ trustee signer',
      reason: 'Trust accounts require at least 1 trustee signer.',
      remediationAction: { label: 'Add trustee', deepLinkStepId: 'people' },
    });
  }

  // CIP on the trustee(s) — we run buildCipGate which targets primary; trustees
  // are exercised through OFAC batch.
  gates.push(buildCipGate(cart));
  gates.push(buildOfacGate(cart));

  // Trust certification document (SPFx TRUST_CERT doc code)
  gates.push({
    gateCode: 'DOC_MISSING_TRUST_CERT',
    entityType: cart.entityType ?? '',
    category: 'documentCollection',
    status: 'warn',
    label: 'Trust certification / 1st & last pages',
    reason: 'Collect trust certification (or first + last pages of trust document).',
    remediationAction: { label: 'Upload trust cert', deepLinkStepId: 'compliance' },
  });

  // Beneficiaries — trust benefits go to named individuals/entities.  v3
  // placeholder — beneficiary entry lives on the ProductsStep BeneficiaryPicker
  // (primary/contingent); GateReport surfaces it as warn until wired.
  gates.push({
    gateCode: 'TRUST_BENEFICIARIES',
    entityType: cart.entityType ?? '',
    category: 'relationship',
    status: 'warn',
    label: 'Trust beneficiaries collected',
    reason: 'Pending: read from BeneficiaryPicker output on each trust product.',
  });

  // CDD auto-exempt for trusts (SPFx CddCipSection AUTO_EXEMPT_TRUST)
  gates.push({
    gateCode: 'CDD',
    entityType: cart.entityType ?? '',
    category: 'compliance',
    status: 'pass',
    label: 'Customer Due Diligence (CDD)',
    reason: 'Auto-exempt — Trust / Estate (Core Director field 378 = 17).',
  });

  return gates;
}
